import { useState, useEffect } from 'react';
import {
    Box,
    Avatar,
    Typography,
    Chip,
    Divider,
    Tooltip,
    Button,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import ErrorHandler from '@Root/ErrorHandler';
import NameCardView from '@RegistriesViews/NameCardView';
import LabelViewCreatedOverTime from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewCreatedOverTime';
import LabelViewUsageList from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewUsageList';
import OrganizationCardService from '@Helpers/URLHelpers/WeboutService/OrganizationCardService';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './NameCardDisplay.scss';

export default function NameCardDisplay(props) {
    const [imageUrl, setImageUrl] = useState('');
    const [showNameCardView, setShowNameCardView] = useState(false);
    const [labelData, setLabelData] = useState();
    const [organizationCard, setOrganizationCard] = useState();
    const organizationCardService = new OrganizationCardService();
    const errorHandler = new ErrorHandler();
    const cn = 'nameCardDisplay';

    useEffect(() => {
        renderData();
    }, [props]); // eslint-disable-line react-hooks/exhaustive-deps

    const renderData = async () => {
        try {
            setLabelData({ ...props.nameCard, registryType: 'nameCard' });
            if (props.nameCard?.photo) {
                setImageUrl(URL.createObjectURL(dataURLtoFile(props.nameCard.photo)));
            } else {
                setImageUrl('');
            }
            if (props.nameCard?.organization && props.nameCard.organization !== 0) {
                let orgCard = await organizationCardService.getById(props.nameCard.organization);
                orgCard = JSON.parse(orgCard.body);
                setOrganizationCard(orgCard);
            } else {
                setOrganizationCard(null);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const dataURLtoFile = (dataurl) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], { type: mime });
    };

    const getInitials = (name) => {
        if (!name) return 'NC';
        const words = name.trim().split(' ');
        if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    return (
        <div className={cn}>
            <Box className={`${cn}__container`}>

                {/* ── Identity card ──────────────────────────────────── */}
                <Box className={`${cn}__card`}>
                    <Box className={`${cn}__card__top`}>
                        <Avatar
                            src={imageUrl}
                            className={`${cn}__avatar`}
                            alt={props.nameCard?.name}
                        >
                            {!imageUrl && getInitials(props.nameCard?.name)}
                        </Avatar>

                        <Box className={`${cn}__card__info`}>
                            <Typography className={`${cn}__name`}>
                                {props.nameCard?.name || 'Unnamed'}
                            </Typography>

                            <Box className={`${cn}__card__meta`}>
                                {props.nameCard?.designation && (
                                    <Chip
                                        icon={<WorkOutlineIcon />}
                                        label={props.nameCard.designation}
                                        size="small"
                                        className={`${cn}__designationChip`}
                                    />
                                )}
                                {organizationCard?.name && (
                                    <Box className={`${cn}__metaRow`}>
                                        <BusinessIcon className={`${cn}__metaRow__icon`} />
                                        <Typography variant="caption" className={`${cn}__metaRow__text`}>
                                            {organizationCard.name}
                                        </Typography>
                                    </Box>
                                )}
                                {props.nameCard?.email && (
                                    <Box className={`${cn}__metaRow`}>
                                        <EmailOutlinedIcon className={`${cn}__metaRow__icon`} />
                                        <Typography variant="caption" className={`${cn}__metaRow__text`}>
                                            {props.nameCard.email}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Actions */}
                        <Box className={`${cn}__card__actions`}>
                            <Tooltip title="Edit card details">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditOutlinedIcon />}
                                    onClick={() => setShowNameCardView(true)}
                                    className={`${cn}__actionBtn ${cn}__actionBtn--edit`}
                                >
                                    Edit
                                </Button>
                            </Tooltip>
                            <Tooltip title="View analytics overview">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<BarChartOutlinedIcon />}
                                    onClick={() => props?.handleLabelAnalytics(props.nameCard)}
                                    className={`${cn}__actionBtn ${cn}__actionBtn--analytics`}
                                >
                                    Analytics
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Extra details row */}
                    {(props.nameCard?.department || props.nameCard?.branch || props.nameCard?.address) && (
                        <>
                            <Divider className={`${cn}__divider`} />
                            <Box className={`${cn}__detailsRow`}>
                                {props.nameCard?.department && (
                                    <Box className={`${cn}__detailPill`}>
                                        <AccountTreeOutlinedIcon className={`${cn}__detailPill__icon`} />
                                        <Box>
                                            <Typography className={`${cn}__detailPill__label`}>Department</Typography>
                                            <Typography className={`${cn}__detailPill__value`}>{props.nameCard.department}</Typography>
                                        </Box>
                                    </Box>
                                )}
                                {props.nameCard?.branch && (
                                    <Box className={`${cn}__detailPill`}>
                                        <BusinessIcon className={`${cn}__detailPill__icon`} />
                                        <Box>
                                            <Typography className={`${cn}__detailPill__label`}>Branch</Typography>
                                            <Typography className={`${cn}__detailPill__value`}>{props.nameCard.branch}</Typography>
                                        </Box>
                                    </Box>
                                )}
                                {props.nameCard?.address && (
                                    <Box className={`${cn}__detailPill`}>
                                        <FmdGoodOutlinedIcon className={`${cn}__detailPill__icon`} />
                                        <Box>
                                            <Typography className={`${cn}__detailPill__label`}>Address</Typography>
                                            <Typography className={`${cn}__detailPill__value`}>{props.nameCard.address}</Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </Box>

                {/* Analytics charts */}
                {labelData && (
                    <Box className={`${cn}__analyticsSection`}>
                        <Box className={`${cn}__analyticsCard`}>
                            <LabelViewCreatedOverTime labelData={labelData} />
                        </Box>
                        <Box className={`${cn}__analyticsCard`}>
                            <LabelViewUsageList labelData={labelData} />
                        </Box>
                    </Box>
                )}
            </Box>

            {showNameCardView && (
                <NameCardView
                    open={showNameCardView}
                    closePopup={() => setShowNameCardView(false)}
                    reloadData={props.reloadData}
                    nameCard={props.nameCard}
                />
            )}
        </div>
    );
}
